import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";

import orleansHero from "../../assets/images/headers/orleans-hero.png";

/**
 * Cinematic homepage hero — full-bleed Vegas night image with brand-led copy.
 */
export default function CinematicHero({
  videoSrc,
  imageSrc = orleansHero,
}) {
  const [shown, setShown] = useState(false);
  useEffect(() => {
    const t = requestAnimationFrame(() => setShown(true));
    return () => cancelAnimationFrame(t);
  }, []);

  return (
    <section className="relative mt-[80px] flex h-[calc(100svh-80px)] w-full flex-col overflow-hidden bg-ink-900 md:mt-0 md:h-auto md:min-h-[88vh] md:justify-end md:pt-0">
      {/* Media — fills remaining space above copy on mobile; full-bleed overlay on desktop */}
      <div className="relative min-h-0 w-full flex-1 overflow-hidden md:absolute md:inset-0 md:flex-none">
        {videoSrc ? (
          <video
            src={videoSrc}
            autoPlay
            muted
            loop
            playsInline
            className="hero-media absolute inset-0 h-full w-full object-cover object-center"
          />
        ) : (
          <img
            src={imageSrc}
            alt="The Orleans Hotel and Casino at night"
            className="hero-media absolute inset-0 h-full w-full object-cover object-center"
          />
        )}

        {/* Desktop atmosphere only — mobile copy sits below the photo */}
        <div
          className="pointer-events-none absolute inset-0 hidden md:block"
          style={{
            background:
              "linear-gradient(180deg, rgba(10,10,10,0.12) 0%, rgba(10,10,10,0.08) 45%, rgba(10,10,10,0.45) 72%, rgba(10,10,10,0.92) 100%)",
          }}
        />
      </div>

      {/* Content — below photo on mobile; overlaid at bottom on desktop */}
      <div
        className={`relative z-10 mx-auto w-full shrink-0 max-w-7xl px-5 pb-10 pt-5 transition-all duration-700 ease-out md:px-10 md:pb-24 md:pt-0 ${
          shown ? "translate-y-0 opacity-100" : "translate-y-4 opacity-0"
        }`}
      >
        <h1 className="whitespace-nowrap text-[clamp(1.35rem,6.2vw,3.75rem)] font-extrabold leading-none tracking-tight text-white">
          On The Strip Ticketing
        </h1>
        <p className="mt-3 max-w-xl text-base leading-relaxed text-white/75 md:mt-4 md:text-lg">
          The best seats to the best shows on the Strip: tribute concerts,
          comedy, and headline productions at The Orleans.
        </p>

        <div
          className={`mt-6 flex flex-col gap-3 transition-all delay-150 duration-700 ease-out sm:flex-row md:mt-8 ${
            shown ? "translate-y-0 opacity-100" : "translate-y-3 opacity-0"
          }`}
        >
          <Link
            to="/events"
            className="inline-flex h-12 items-center justify-center rounded-lg bg-brand-blue px-8 font-semibold text-white transition-colors hover:bg-brand-blue-hover focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-blue-bright focus-visible:ring-offset-2 focus-visible:ring-offset-ink-900"
          >
            Browse shows
          </Link>
          <Link
            to="/dinner-and-show"
            className="inline-flex h-12 items-center justify-center rounded-lg border border-white px-8 font-semibold text-white transition-colors hover:border-white hover:bg-white/5 focus:outline-none focus-visible:ring-2 focus-visible:ring-white/50 focus-visible:ring-offset-2 focus-visible:ring-offset-ink-900"
          >
            Dinner &amp; a show
          </Link>
        </div>
      </div>

      {/* Scroll cue — desktop overlay hero only */}
      <div className="pointer-events-none absolute bottom-5 left-1/2 z-10 hidden -translate-x-1/2 md:block">
        <svg
          viewBox="0 0 24 24"
          className="h-6 w-6 animate-bounce text-white/40"
          fill="none"
          aria-hidden
        >
          <path
            d="M6 9l6 6 6-6"
            stroke="currentColor"
            strokeWidth="1.6"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </div>

      <style>{`
        /* Mobile: no extra zoom — show top and street in frame */
        @media (max-width: 767px) {
          .hero-media {
            transform: none;
          }
        }
      `}</style>
    </section>
  );
}
